import { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Empty,
  Field,
  Grid,
  KeyIcon,
  LinkIcon,
  Note,
  Panel,
  RefreshIcon,
  Spinner,
  StateBadge,
  Stat,
  TableWrap,
  shortId,
} from '@wyocspay/ui';
import { dateTime, money, relative } from './format.js';
import {
  ApiError,
  api,
  type Delivery,
  type MethodRecommendation,
  type Overview,
  type PaymentDetail,
  type PaymentFees,
  type PublicPayment,
} from './api.js';
import { PaymentLinks } from './NewPayment.js';
import { RefundDialog } from './RefundDialog.js';
import { AccountNotice } from './money-views.js';
import { TimelineData, actionLabel } from './timeline.js';

/** Small helper so every view gets the same loading / error treatment. */
function useLoad<T>(load: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await load());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    void run();
  }, [run]);

  return { data, error, loading, reload: run };
}

function Frame({
  loading,
  error,
  children,
}: {
  loading: boolean;
  error: string | null;
  children: React.ReactNode;
}) {
  if (loading) return <Spinner label="Loading…" />;
  if (error) return <Note tone="danger">{error}</Note>;
  return <>{children}</>;
}

// ---------------------------------------------------------------------------

export function OverviewView({
  onOpen,
  onOpenVerification,
}: {
  onOpen: (id: string) => void;
  onOpenVerification: () => void;
}) {
  const { data, error, loading } = useLoad<Overview>(() => api.overview());

  return (
    <Frame loading={loading} error={error}>
      {data ? (
        <div style={{ display: 'grid', gap: 18 }}>
          {/*
            One notice, or none. `AccountNotice` decides which, and renders
            nothing at all when Stripe wants nothing - see `account-state.ts`.
          */}
          <AccountNotice merchant={data.merchant} onOpenVerification={onOpenVerification} />

          <Grid cols={4}>
            <Stat
              label="Volume · 30 days"
              value={money(data.stats.volume_30d, data.merchant.currency)}
              hint={`${data.stats.succeeded_30d} settled payments`}
              accent="var(--wc-green)"
            />
            <Stat label="Payments · 30 days" value={data.stats.payments_30d} hint="All states" />
            <Stat
              label="Success rate"
              value={data.stats.success_rate === null ? '-' : `${data.stats.success_rate}%`}
              hint="Settled ÷ created"
              accent="var(--wc-amber)"
            />
            {/*
              One number, and it is the whole number.

              This tile used to read "Platform fee" and show the platform's own
              percentage beside a fixed amount that already had the processor's
              share added in - one half combined and the other not, in the same
              sentence. Whichever way a merchant read it, it was wrong, and it
              was wrong in the direction that flatters us.
            */}
            {data.fees.charges > 0 ? (
              <Stat
                label="Fees · 30 days"
                value={money(data.fees.actual_total, data.fees.currency)}
                hint={
                  data.fees.effective_percent_bps !== null
                    ? `${(data.fees.effective_percent_bps / 100).toFixed(1)}% of ${money(data.fees.volume, data.fees.currency)} · ${data.fees.charges} settled`
                    : `${data.fees.charges} settled`
                }
                accent="var(--wc-grey)"
              />
            ) : (
              <Stat
                label="Total per charge"
                value={`${(data.rates.combined_percent_bps / 100).toFixed(2)}%`}
                hint={`+ ${money(data.rates.combined_fixed, data.rates.currency)} per charge`}
                accent="var(--wc-grey)"
              />
            )}
          </Grid>

          <p style={{ margin: '-8px 0 0', fontSize: 12.5, color: 'var(--wc-faint)', lineHeight: 1.55 }}>
            {data.fees.charges > 0 ? (
              <>
                That is what Stripe and the platform fee actually took from your settled payments over
                the last 30 days, read back from Stripe - not an estimate. The cost per charge varies
                with the card type, the country that issued it and whether a currency was converted, so
                a small charge carries the fixed fee more heavily. Open any payment to see its exact
                cost.
              </>
            ) : (
              <>
                The rate above is an indication, based on Stripe's published rate plus ours. What
                Stripe actually deducts varies with the card type, the country that issued it and
                whether a currency was converted, so an American Express or an overseas card costs more
                than a domestic one. Open any payment to see what that charge really cost.
              </>
            )}
          </p>

          <Panel title="Recent payments" padded={false}>
            {data.recent.length === 0 ? (
              <Empty>No payments yet.</Empty>
            ) : (
              <PaymentTable rows={data.recent} onOpen={onOpen} />
            )}
          </Panel>
        </div>
      ) : null}
    </Frame>
  );
}

// ---------------------------------------------------------------------------

function PaymentTable({ rows, onOpen }: { rows: PublicPayment[]; onOpen: (id: string) => void }) {
  return (
    <TableWrap>
      <table className="wc-table">
        <thead>
          <tr>
            <th>Payment</th>
            <th>Reference</th>
            <th>Description</th>
            <th>Status</th>
            <th>Created</th>
            <th className="wc-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr
              key={p.id}
              onClick={() => onOpen(p.id)}
              style={{ cursor: 'pointer' }}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onOpen(p.id);
              }}
            >
              <td className="wc-num" style={{ fontSize: 12.5 }}>{shortId(p.id)}</td>
              <td className="wc-num" style={{ fontSize: 12.5 }}>{p.merchant_reference ?? '-'}</td>
              <td style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {p.description ?? '-'}
              </td>
              <td><StateBadge state={p.status} /></td>
              <td style={{ color: 'var(--wc-muted)', fontSize: 13 }}>{relative(p.created_at)}</td>
              <td className="wc-right wc-num" style={{ fontWeight: 600 }}>
                {money(p.amount, p.currency)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableWrap>
  );
}

export function PaymentsView({ onOpen }: { onOpen: (id: string) => void }) {
  const [state, setState] = useState('all');
  const [search, setSearch] = useState('');
  const { data, error, loading } = useLoad(() => api.payments({ state, search }), [state, search]);

  const states = ['all', 'CREATED', 'PROCESSING', 'SUCCEEDED', 'FAILED', 'REFUNDED', 'DISPUTED'];

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {states.map((s) => (
          <button
            key={s}
            type="button"
            className={`wc-btn wc-btn-sm ${state === s ? '' : 'wc-btn-secondary'}`}
            onClick={() => setState(s)}
          >
            {s === 'all' ? 'All' : s.replace(/_/g, ' ')}
          </button>
        ))}
        <input
          className="wc-input"
          placeholder="Search id, reference or description"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 300, marginLeft: 'auto' }}
        />
      </div>

      <Frame loading={loading} error={error}>
        <Panel padded={false}>
          {data && data.data.length > 0 ? (
            <PaymentTable rows={data.data} onOpen={onOpen} />
          ) : (
            <Empty>No payments match.</Empty>
          )}
        </Panel>
      </Frame>
    </div>
  );
}

// ---------------------------------------------------------------------------

/** Method keys the recommender speaks, made readable without a locale file. */
function methodLabel(key: string): string {
  const known: Record<string, string> = {
    card: 'Card',
    link: 'Link',
    apple_pay: 'Apple Pay',
    google_pay: 'Google Pay',
    ach_debit: 'ACH direct debit',
    sepa_debit: 'SEPA direct debit',
    bacs_debit: 'Bacs direct debit',
    pay_by_bank: 'Pay by bank',
    bank_transfer: 'Bank transfer',
  };
  return known[key] ?? key.replace(/_/g, ' ');
}

/** One component of the fee, rendered only when the server actually returned it. */
function FeeRow({ label, amount, currency }: { label: string; amount: number | null | undefined; currency: string }) {
  if (amount == null) return null;
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 12,
        padding: '7px 0',
        borderBottom: '1px solid var(--wc-border)',
        fontSize: 13.5,
      }}
    >
      <dt style={{ color: 'var(--wc-muted)' }}>{label}</dt>
      <dd className="wc-num" style={{ margin: 0 }}>
        {money(amount, currency)}
      </dd>
    </div>
  );
}

/**
 * What a charge really cost, and the cheapest way it could have been taken.
 *
 * Self-contained, like PaymentLinks: it loads on its own so a fee lookup that is
 * slow or fails never blanks the payment page around it. Every figure is the
 * server's - the panel formats minor units and renders, and does no fee
 * arithmetic of its own. The estimate is labelled as such and only replaced once
 * Stripe's real deduction has settled; a charge with no snapshot yet says so
 * rather than showing zeroes as if they were final.
 */
/** Human brand name from Stripe's lowercase brand slug. */
const CARD_BRAND_NAMES: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  american_express: 'American Express',
  discover: 'Discover',
  diners: 'Diners Club',
  diners_club: 'Diners Club',
  jcb: 'JCB',
  unionpay: 'UnionPay',
  eftpos_au: 'Eftpos',
};
function prettyBrand(brand: string | null | undefined): string {
  if (!brand) return 'Card';
  return CARD_BRAND_NAMES[brand.toLowerCase()] ?? brand.charAt(0).toUpperCase() + brand.slice(1);
}

/** Human name for the wallet a card was presented through. */
const WALLET_NAMES: Record<string, string> = {
  apple_pay: 'Apple Pay',
  google_pay: 'Google Pay',
  samsung_pay: 'Samsung Pay',
  link: 'Link',
  visa_checkout: 'Visa Checkout',
  masterpass: 'Masterpass',
  amex_express_checkout: 'Amex Express Checkout',
};
function prettyWallet(w: string | null | undefined): string | null {
  if (!w) return null;
  return WALLET_NAMES[w.toLowerCase()] ?? w.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
/** File slug for the wallet logo under /brands (apple_pay -> apple-pay). */
function walletSlug(w: string | null | undefined): string {
  const s = (w ?? '').toLowerCase().replace(/_/g, '-');
  return /^[a-z0-9-]+$/.test(s) ? s : 'wallet';
}

/** Full country name from a two-letter code, falling back to the code itself. */
function regionName(code: string | null | undefined): string | null {
  if (!code) return null;
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(code.toUpperCase()) ?? code.toUpperCase();
  } catch {
    return code.toUpperCase();
  }
}

/**
 * The flag for a two-letter country code, as the two regional-indicator code
 * points. Renders as a flag on Apple and Android; Windows shows the letters,
 * which is why the country name always sits beside it. No asset, no request.
 */
function flagEmoji(code: string | null | undefined): string | null {
  if (!code || !/^[a-zA-Z]{2}$/.test(code)) return null;
  const cc = code.toUpperCase();
  return String.fromCodePoint(0x1f1e6 + cc.charCodeAt(0) - 65, 0x1f1e6 + cc.charCodeAt(1) - 65);
}

/**
 * A real brand image from `/brands/<slug>.(svg|png)` if one has been dropped in,
 * otherwise a drawn fallback. It tries SVG first, then PNG, then hands over to
 * `fallback` - so the panel looks right today with the built-in marks and
 * upgrades to real artwork the moment a file appears, with no code change.
 */
function BrandImg({
  slug,
  alt,
  fallback,
  height,
  maxWidth,
}: {
  slug: string;
  alt: string;
  fallback: React.ReactNode;
  height: number;
  maxWidth?: number;
}) {
  // 0 = try .svg, 1 = try .png, 2 = give up and draw the fallback.
  const [stage, setStage] = useState<0 | 1 | 2>(0);
  if (stage === 2) return <>{fallback}</>;
  return (
    <img
      src={`/brands/${slug}.${stage === 0 ? 'svg' : 'png'}`}
      alt={alt}
      onError={() => setStage((s) => (s + 1) as 0 | 1 | 2)}
      style={{ height, maxWidth: maxWidth ?? height * 1.6, width: 'auto', objectFit: 'contain', display: 'block' }}
    />
  );
}

/** Slug used for the brand image file, normalised to Stripe's brand names. */
function brandSlug(brand: string | null | undefined): string {
  const b = (brand ?? '').toLowerCase();
  if (b === 'american_express') return 'amex';
  return /^[a-z0-9_]+$/.test(b) ? b : 'generic';
}

/**
 * The card network's mark: real image if present, else the drawn fallback. It
 * is wrapped so a glossy highlight sweeps across it - the "shine" that reads as
 * a physical card catching the light. The sweep respects reduced-motion.
 */
function CardBrandMark({ brand }: { brand: string | null | undefined }) {
  return (
    <span className="wc-cardmark" style={{ display: 'inline-block', height: 30, lineHeight: 0 }}>
      <BrandImg
        slug={brandSlug(brand)}
        alt={prettyBrand(brand)}
        height={30}
        maxWidth={46}
        fallback={<InlineBrandMark brand={brand} />}
      />
    </span>
  );
}

/**
 * The card network's own mark, drawn as inline SVG so it needs no assets and no
 * network - the fallback when no real image has been supplied. A recognised
 * brand gets its logo; anything else gets a neutral card chip.
 */
function InlineBrandMark({ brand }: { brand: string | null | undefined }) {
  const b = (brand ?? '').toLowerCase();
  const box = { width: 40, height: 26, display: 'block' } as const;
  // The chip is drawn by the .wc-cardmark wrapper now, so the marks are just the
  // logo itself on a transparent ground - no self-drawn frame to double it up.
  const frame = null;

  if (b === 'visa') {
    return (
      <svg viewBox="0 0 46 30" style={box} role="img" aria-label="Visa">
        {frame}
        <text x="23" y="20" textAnchor="middle" fontFamily="Arial, sans-serif" fontSize="12.5" fontWeight="700" fontStyle="italic" letterSpacing="0.3" fill="#1434CB">VISA</text>
      </svg>
    );
  }
  if (b === 'mastercard') {
    return (
      <svg viewBox="0 0 46 30" style={box} role="img" aria-label="Mastercard">
        {frame}
        <circle cx="19.5" cy="15" r="7" fill="#EB001B" />
        <circle cx="26.5" cy="15" r="7" fill="#F79E1B" />
        <path d="M23 9.35a7 7 0 0 1 0 11.3 7 7 0 0 1 0-11.3Z" fill="#FF5F00" />
      </svg>
    );
  }
  if (b === 'amex' || b === 'american_express') {
    return (
      <svg viewBox="0 0 46 30" style={box} role="img" aria-label="American Express">
        <rect x="0.5" y="0.5" width="45" height="29" rx="5" fill="#1F72CD" />
        <text x="23" y="18.5" textAnchor="middle" fontFamily="Arial, sans-serif" fontSize="8" fontWeight="700" letterSpacing="0.5" fill="#fff">AMEX</text>
      </svg>
    );
  }
  if (b === 'discover') {
    return (
      <svg viewBox="0 0 46 30" style={box} role="img" aria-label="Discover">
        {frame}
        <text x="6" y="19" fontFamily="Arial, sans-serif" fontSize="7" fontWeight="700" fill="#1a1a1a">Discover</text>
        <circle cx="38" cy="18" r="5.5" fill="#FF6000" />
      </svg>
    );
  }
  // Neutral card glyph for JCB, UnionPay, Diners and anything unknown - the
  // wrapper supplies the chip, so this is just the stripes.
  return (
    <svg viewBox="0 0 46 30" style={box} role="img" aria-label={brand ? prettyBrand(brand) : 'Card'}>
      <rect x="4" y="8" width="38" height="14" rx="2.5" fill="none" stroke="#c4ccd2" strokeWidth="1.5" />
      <rect x="4" y="12" width="38" height="3.4" fill="#c4ccd2" />
    </svg>
  );
}

/** The small contactless-waves glyph for an in-person tap. */
function ContactlessIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden style={{ flex: 'none' }}>
      <path d="M7 8.5a6 6 0 0 1 0 7M11 6a10 10 0 0 1 0 12M15 3.5a14 14 0 0 1 0 17" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

/**
 * The Tap to Pay lockup for the device that took the payment: the real badge
 * from `/brands/tap-to-pay-<platform>.(svg|png)` when supplied, otherwise the
 * contactless glyph and the words. The platform comes from the terminal, so an
 * iPhone tap and an Android tap read differently.
 */
function TapToPayBadge({ platform }: { platform: 'ios' | 'android' | null }) {
  const label = platform === 'android' ? 'Tap to Pay on Android' : 'Tap to Pay on iPhone';
  return (
    <span className="wc-neon-badge">
      <ContactlessIcon />
      {label}
    </span>
  );
}

/**
 * What the customer paid with, presented the way Stripe presents it: the card
 * network's own logo, the last four digits large, and the funding, issuing
 * country, domestic/international split and channel laid out as tiles. Every
 * field is optional - only what the payment actually carries is drawn, so a
 * bank charge or a card that has not settled its detail yet does not show
 * blanks. A brand and four digits are not card data; nothing sensitive is
 * shown or stored.
 */
function PaymentMethodPanel({ detail }: { detail: PaymentDetail }) {
  const card = detail.card;
  const methodType = detail.payment.payment_method_type;
  const cap = (s: string | null | undefined) =>
    s ? s.charAt(0).toUpperCase() + s.slice(1) : null;

  const hasCard = !!(card?.brand || card?.last4);
  const isCardMethod = hasCard || methodType === 'card' || methodType === 'card_present';

  // Nothing to say (a bank charge, or a card charge with no detail yet): quiet.
  if (!isCardMethod && !methodType) return null;

  const inPerson = !!(detail.tap_to_pay || detail.card_present);
  const platform: 'ios' | 'android' | null =
    detail.device_platform === 'android' ? 'android' : detail.device_platform === 'ios' ? 'ios' : null;
  const channelLabel = detail.tap_to_pay
    ? platform === 'android' ? 'Tap to Pay on Android' : 'Tap to Pay on iPhone'
    : detail.card_present ? 'In person' : 'Online';
  const fundingLabel = cap(card?.funding);
  const typeLabel = card?.category
    ? card.category === 'international' ? 'International'
    : card.category === 'premium' ? 'Premium / commercial'
    : 'Domestic'
    : null;
  const countryLabel = regionName(card?.country);
  const walletName = prettyWallet(card?.wallet);

  // The line under the brand name: the wallet it was presented through (if any),
  // how it was funded, and how it was taken.
  const subParts = [walletName, fundingLabel, hasCard ? channelLabel : null].filter(Boolean) as string[];

  const tiles: Array<{ label: string; value: React.ReactNode }> = [];
  if (walletName)
    tiles.push({
      label: 'Wallet',
      value: (
        <BrandImg
          slug={walletSlug(card?.wallet)}
          alt={walletName}
          height={16}
          maxWidth={96}
          fallback={<span>{walletName}</span>}
        />
      ),
    });
  if (fundingLabel) tiles.push({ label: 'Funding', value: fundingLabel });
  if (typeLabel) tiles.push({
    label: 'Card type',
    value: typeLabel === 'International'
      ? <span style={{ color: 'var(--wc-amber)' }}>{typeLabel}</span>
      : typeLabel,
  });
  if (countryLabel) {
    const flag = flagEmoji(card?.country);
    tiles.push({
      label: 'Issuing country',
      value: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {flag ? <span aria-hidden style={{ fontSize: 16, lineHeight: 1 }}>{flag}</span> : null}
          {countryLabel}
        </span>
      ),
    });
  }
  tiles.push({
    label: 'Channel',
    value: detail.tap_to_pay ? (
      <TapToPayBadge platform={platform} />
    ) : (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
        {inPerson ? <ContactlessIcon /> : null}
        {channelLabel}
      </span>
    ),
  });

  const brandName = hasCard ? prettyBrand(card?.brand) : methodType ? methodLabel(methodType) : 'Card';

  return (
    <Panel title="Payment method">
      {/* Hero: the card, as it would read off the plastic. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, paddingBottom: 16, borderBottom: '1px solid var(--wc-border)' }}>
        <CardBrandMark brand={hasCard ? card?.brand : null} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--wc-ink)', display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <span>{brandName}</span>
            {card?.last4 ? (
              <span className="wc-num" aria-label={`ending in ${card.last4}`} style={{ fontWeight: 600, letterSpacing: '0.5px' }}>
                <span aria-hidden>••••&nbsp;</span>{card.last4}
              </span>
            ) : null}
          </div>
          {subParts.length ? (
            <div style={{ marginTop: 3, fontSize: 13, color: 'var(--wc-muted)' }}>{subParts.join(' · ')}</div>
          ) : null}
        </div>
      </div>

      {/* Property tiles, hairline-separated like Stripe's detail grid. */}
      {tiles.length ? (
        <div
          style={{
            marginTop: 16,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: 1,
            background: 'var(--wc-border)',
            border: '1px solid var(--wc-border)',
            borderRadius: 6,
            overflow: 'hidden',
          }}
        >
          {tiles.map((t) => (
            <div key={t.label} style={{ background: 'var(--wc-surface)', padding: '10px 12px' }}>
              <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--wc-faint)', fontWeight: 600 }}>{t.label}</div>
              <div style={{ marginTop: 3, fontSize: 14, fontWeight: 500, color: 'var(--wc-ink)' }}>{t.value}</div>
            </div>
          ))}
        </div>
      ) : null}
    </Panel>
  );
}

function PaymentFeesSection({ paymentId, fallbackCurrency }: { paymentId: string; fallbackCurrency: string }) {
  const { data, error, loading } = useLoad<PaymentFees>(() => api.paymentFees(paymentId), [paymentId]);
  // Optional and best-effort: an unpriced or unavailable recommendation shows nothing.
  const rec = useLoad<MethodRecommendation>(() => api.recommendedMethod(paymentId), [paymentId]);

  if (loading) {
    return (
      <Panel title="Fees">
        <Spinner label="Loading fees…" />
      </Panel>
    );
  }
  if (error || !data) {
    return (
      <Panel title="Fees">
        <Empty>{error ?? 'No fee information for this payment.'}</Empty>
      </Panel>
    );
  }

  const ccy = data.currency ?? fallbackCurrency;
  // The estimate stands in until Stripe's real deduction is read back; a null
  // reconciled_at means the same thing even if the status has not caught up.
  const awaiting = data.status === 'estimated' || data.reconciled_at == null;

  const statusBadge =
    data.status === 'unknown' ? (
      <Badge tone="neutral" icon={false}>Not settled yet</Badge>
    ) : awaiting ? (
      <Badge tone="pending">Estimated / awaiting settlement</Badge>
    ) : (
      <Badge tone="ok">Settled</Badge>
    );

  const recommendation =
    rec.data && rec.data.recommended && rec.data.savingsVsBaseline != null && rec.data.savingsVsBaseline > 0
      ? rec.data
      : null;

  return (
    <Panel title="Fees" actions={statusBadge}>
      {data.status === 'unknown' ? (
        <Note>
          The final cost of this charge is not available yet. It appears here once Stripe has settled
          the charge and reported the fee it actually took.
        </Note>
      ) : (
        <>
          <Grid cols={4}>
            <Stat
              label="Estimated fee"
              value={data.estimated_total_fee != null ? money(data.estimated_total_fee, ccy) : '-'}
              hint="Worked out up front"
            />
            <Stat
              label="Actual fee"
              value={
                !awaiting && data.actual_total_fee != null ? (
                  money(data.actual_total_fee, ccy)
                ) : (
                  <span style={{ color: 'var(--wc-faint)', fontSize: 15 }}>Awaiting settlement</span>
                )
              }
              hint={data.reconciled_at ? `Settled ${relative(data.reconciled_at)}` : "Stripe's own figure"}
              accent="var(--wc-green)"
            />
            <Stat
              label="Difference"
              value={
                !awaiting && data.difference != null ? (
                  money(data.difference, ccy)
                ) : (
                  <span style={{ color: 'var(--wc-faint)', fontSize: 15 }}>-</span>
                )
              }
              hint="Actual vs estimate"
              accent="var(--wc-amber)"
            />
            <Stat
              label="Your net"
              value={data.merchant_net != null ? money(data.merchant_net, ccy) : '-'}
              hint="After all fees"
            />
          </Grid>

          <dl style={{ margin: '14px 0 0' }}>
            <FeeRow label="Processor fee" amount={data.processor_fee} currency={ccy} />
            <FeeRow label="Platform fee" amount={data.platform_fee} currency={ccy} />
            <FeeRow label="Currency conversion" amount={data.fx_fee} currency={ccy} />
          </dl>

          {data.component_breakdown_available === false ? (
            <p style={{ margin: '10px 0 0', fontSize: 12.5, color: 'var(--wc-faint)' }}>
              Stripe reported a total for this charge but not a component-by-component split.
            </p>
          ) : null}

          {data.card && (data.card.brand || data.card.country || data.card.category) ? (
            <p style={{ margin: '12px 0 0', fontSize: 13, color: 'var(--wc-muted)' }}>
              Card: {[data.card.brand, data.card.country, data.card.category].filter(Boolean).join(' · ')}
            </p>
          ) : null}
        </>
      )}

      {recommendation ? (
        <div style={{ marginTop: 14 }}>
          <Note tone="ok" title="Cheapest accepted method">
            {methodLabel(recommendation.recommended!)} would have cost{' '}
            {money(recommendation.savingsVsBaseline!, ccy)}
            {recommendation.savingsPercent != null ? ` (${recommendation.savingsPercent}%)` : ''} less
            than {methodLabel(recommendation.baselineMethod)} on a charge like this.
          </Note>
        </div>
      ) : null}
    </Panel>
  );
}

export function PaymentDetailView({ id, onBack }: { id: string; onBack: () => void }) {
  const { data, error, loading, reload } = useLoad<PaymentDetail>(() => api.payment(id), [id]);
  const [refunding, setRefunding] = useState(false);

  // Refundable only once Stripe has actually settled the charge - offering the
  // button earlier would just produce an error the merchant cannot act on.
  const refundable =
    data && ['SUCCEEDED', 'PARTIALLY_REFUNDED', 'DISPUTED'].includes(data.payment.status) &&
    data.payment.amount_refunded < data.payment.amount;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <button type="button" className="wc-btn wc-btn-secondary wc-btn-sm" onClick={onBack}>
          ← Back to payments
        </button>
        {refundable && !refunding ? (
          <button type="button" className="wc-btn wc-btn-sm wc-btn-danger" onClick={() => setRefunding(true)}>
            Refund
          </button>
        ) : null}
      </div>

      {refunding && data ? (
        <Panel title="Refund this payment">
          <RefundDialog
            paymentId={data.payment.id}
            amount={data.payment.amount}
            amountRefunded={data.payment.amount_refunded}
            currency={data.payment.currency}
            onCancel={() => setRefunding(false)}
            onDone={async () => {
              setRefunding(false);
              await reload();
            }}
          />
        </Panel>
      ) : null}

      <Frame loading={loading} error={error}>
        {data ? (
          <>
            <Grid cols={4}>
              <Stat label="Amount" value={money(data.payment.amount, data.payment.currency)} />
              <Stat
                label="Status"
                value={<StateBadge state={data.payment.status} />}
                hint={data.payment.paid_at ? `Confirmed ${relative(data.payment.paid_at)}` : 'Not settled'}
              />
              <Stat
                label="Refunded"
                value={money(data.payment.amount_refunded, data.payment.currency)}
                hint={data.payment.refund_status}
                accent="var(--wc-blue)"
              />
              <Stat
                label="Attempts"
                value={data.attempts.length}
                hint="Every try is kept"
                accent="var(--wc-amber)"
              />
            </Grid>

            <PaymentMethodPanel detail={data} />

            <PaymentFeesSection paymentId={data.payment.id} fallbackCurrency={data.payment.currency} />

            <PaymentLinks paymentId={data.payment.id} currency={data.payment.currency} />

            <Panel title="Payment attempts" padded={false}>
              <TableWrap>
                <table className="wc-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Status</th>
                      <th>Method</th>
                      <th>Failure</th>
                      <th>Started</th>
                      <th>Settled</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.attempts.length === 0 ? (
                      <tr><td colSpan={6}><Empty>No attempt has been started yet.</Empty></td></tr>
                    ) : (
                      data.attempts.map((a) => (
                        <tr key={a.id}>
                          <td className="wc-num">{a.sequence}</td>
                          <td><StateBadge state={a.status} /></td>
                          <td>{a.payment_method_type ?? '-'}</td>
                          <td style={{ fontSize: 13, color: 'var(--wc-muted)' }}>
                            {a.decline_code ?? a.failure_code
                              ? <span className="wc-num">{a.decline_code ?? a.failure_code}</span>
                              : '-'}
                            {a.failure_message ? <div style={{ fontSize: 12.5 }}>{a.failure_message}</div> : null}
                          </td>
                          <td style={{ fontSize: 13, color: 'var(--wc-muted)' }}>{dateTime(a.created_at)}</td>
                          <td style={{ fontSize: 13, color: 'var(--wc-muted)' }}>{dateTime(a.settled_at)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </TableWrap>
            </Panel>

            <Panel title="Lifecycle audit">
              <p style={{ marginTop: 0, fontSize: 13, color: 'var(--wc-muted)' }}>
                Every state change, with where it came from. Entries written by{' '}
                <code>stripe_webhook</code> are the only ones that can mark money as received.
              </p>
              <ol style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {data.timeline.map((t, i) => (
                  <li
                    key={i}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '150px 1fr',
                      gap: 12,
                      padding: '9px 0',
                      borderBottom: i === data.timeline.length - 1 ? 'none' : '1px solid var(--wc-border)',
                      fontSize: 13.5,
                    }}
                  >
                    <span className="wc-num" style={{ color: 'var(--wc-faint)', fontSize: 12 }}>
                      {dateTime(t.at)}
                    </span>
                    <span style={{ minWidth: 0 }}>
                      {/*
                        The machine name is kept, in the tooltip, because that is
                        what a support conversation quotes. What is read is the
                        sentence.
                      */}
                      <strong title={t.action}>{actionLabel(t.action)}</strong>
                      <Badge tone={t.actor === 'stripe_webhook' ? 'ok' : 'neutral'}>{t.actor}</Badge>
                      <TimelineData data={t.data} currency={data.payment.currency} />
                    </span>
                  </li>
                ))}
              </ol>
            </Panel>
          </>
        ) : null}
      </Frame>
    </div>
  );
}

// ---------------------------------------------------------------------------

export function DevelopersView() {
  const keys = useLoad(() => api.apiKeys());
  const endpoints = useLoad(() => api.endpoints());
  const [issued, setIssued] = useState<{ key: string; prefix: string } | null>(null);
  const [secret, setSecret] = useState<{ secret: string; expires: string | null } | null>(null);
  const [keyName, setKeyName] = useState('');
  const [keyMode, setKeyMode] = useState<'test' | 'live'>('test');
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function issue(): Promise<void> {
    const livemode = keyMode === 'live';
    if (livemode && !window.confirm('Issue a LIVE key? It can create real payments. Store it only on your server.')) return;
    setBusy(true); setErr(null);
    try {
      setIssued(await api.createApiKey(keyName || 'Untitled key', livemode));
      setKeyName('');
      await keys.reload();
    } catch (e) { setErr(e instanceof ApiError ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  async function addEndpoint(): Promise<void> {
    setBusy(true); setErr(null);
    try {
      const created = await api.createEndpoint(url);
      setSecret({ secret: created.secret, expires: null });
      setUrl('');
      await endpoints.reload();
    } catch (e) { setErr(e instanceof ApiError ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  async function rotate(id: string, overlap: number): Promise<void> {
    setBusy(true); setErr(null);
    try {
      const r = await api.rotateSecret(id, overlap);
      setSecret({ secret: r.secret, expires: r.old_secret_expires_at });
      await endpoints.reload();
    } catch (e) { setErr(e instanceof ApiError ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {err ? <Note tone="danger">{err}</Note> : null}

      {issued ? (
        <Note tone="warn" title="Copy this key now">
          This is the only time it is shown. Only a hash of it is stored, so it cannot be recovered.
          <pre className="wc-num" style={{ margin: '8px 0 0', fontSize: 12.5, wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>
            {issued.key}
          </pre>
        </Note>
      ) : null}

      {secret ? (
        <Note tone="warn" title="New webhook signing secret">
          {secret.expires
            ? `The previous secret keeps working until ${dateTime(secret.expires)}, so you can deploy without downtime.`
            : 'Store this now - it is not shown again.'}
          <pre className="wc-num" style={{ margin: '8px 0 0', fontSize: 12.5, wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>
            {secret.secret}
          </pre>
        </Note>
      ) : null}

      <Panel title="API keys" padded={false}>
        <div className="api-key-create-region" style={{ padding: 16 }}>
          <div className="api-key-create">
            <label className="wc-label api-key-name-label" htmlFor="api-key-name">Key name</label>
            <input id="api-key-name" aria-describedby="api-key-name-hint" className="wc-input api-key-name-input" value={keyName} onChange={(e) => setKeyName(e.target.value)} placeholder="Production server" />
            <span id="api-key-name-hint" className="api-key-hint api-key-name-hint">Use one key per server or integration.</span>
            <label className="wc-label api-key-mode-label" htmlFor="api-key-mode">Environment</label>
            <select id="api-key-mode" aria-describedby="api-key-mode-hint" className="wc-input api-key-mode-input" value={keyMode} onChange={(e) => setKeyMode(e.target.value as 'test' | 'live')}>
              <option value="test">TEST - sandbox</option>
              <option value="live">LIVE - real payments</option>
            </select>
            <span id="api-key-mode-hint" className="api-key-hint api-key-mode-hint">Live keys can create real payments.</span>
            <button type="button" className="wc-btn api-key-issue" onClick={() => void issue()} disabled={busy}>
              <KeyIcon size={14} /> Issue {keyMode} key
            </button>
          </div>
          {keyMode === 'live' ? (
            <div style={{ marginTop: 12 }}>
              <Note tone="warn" title="Live credential">
                This key authorises production API requests and must stay on your server. Never put it in browser code, a mobile app, source control, or support messages.
              </Note>
            </div>
          ) : null}
        </div>

        <Frame loading={keys.loading} error={keys.error}>
          <TableWrap>
            <table className="wc-table">
              <thead>
                <tr><th>Name</th><th>Prefix</th><th>Mode</th><th>Last used</th><th>Status</th><th /></tr>
              </thead>
              <tbody>
                {keys.data?.data.length === 0 ? (
                  <tr><td colSpan={6}><Empty>No keys issued.</Empty></td></tr>
                ) : keys.data?.data.map((k) => (
                  <tr key={k.id}>
                    <td>{k.name}</td>
                    <td className="wc-num" style={{ fontSize: 12.5 }}>{k.prefix}…</td>
                    <td><Badge tone={k.livemode ? 'warn' : 'neutral'} icon={false}>{k.livemode ? 'LIVE' : 'TEST'}</Badge></td>
                    <td style={{ fontSize: 13, color: 'var(--wc-muted)' }}>{k.lastUsedAt ? relative(k.lastUsedAt) : 'never'}</td>
                    <td>{k.revokedAt ? <Badge tone="fail">REVOKED</Badge> : <Badge tone="ok">ACTIVE</Badge>}</td>
                    <td className="wc-right">
                      {!k.revokedAt ? (
                        <button
                          type="button"
                          className="wc-btn wc-btn-sm wc-btn-secondary"
                          onClick={async () => { await api.revokeApiKey(k.id); await keys.reload(); }}
                        >
                          Revoke
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        </Frame>
      </Panel>

      <Panel title="Webhook endpoints" padded={false}>
        <div style={{ padding: 16 }}>
          <Field
            label="Endpoint URL"
            hint="HTTPS only. Payloads are signed with HMAC-SHA-256."
            action={
              <button type="button" className="wc-btn" onClick={addEndpoint} disabled={busy || !url}>
                <LinkIcon size={14} /> Add endpoint
              </button>
            }
          >
            <input className="wc-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com/wyocspay/webhooks" />
          </Field>
        </div>

        <Frame loading={endpoints.loading} error={endpoints.error}>
          <TableWrap>
            <table className="wc-table">
              <thead>
                <tr><th>URL</th><th>Status</th><th>Secrets</th><th /></tr>
              </thead>
              <tbody>
                {endpoints.data?.data.length === 0 ? (
                  <tr><td colSpan={4}><Empty>No endpoints configured.</Empty></td></tr>
                ) : endpoints.data?.data.map((e) => (
                  <tr key={e.id}>
                    <td style={{ fontSize: 13 }}>{e.url}</td>
                    <td><Badge tone={e.status === 'ACTIVE' ? 'ok' : 'neutral'}>{e.status}</Badge></td>
                    <td>
                      {e.active_secrets}
                      {e.rotation_in_progress ? <Badge tone="pending">ROTATING</Badge> : null}
                    </td>
                    <td className="wc-right" style={{ whiteSpace: 'nowrap' }}>
                      <button type="button" className="wc-btn wc-btn-sm wc-btn-secondary" onClick={() => rotate(e.id, 86400)} disabled={busy}>
                        Rotate (24h overlap)
                      </button>{' '}
                      <button type="button" className="wc-btn wc-btn-sm wc-btn-danger" onClick={() => rotate(e.id, 0)} disabled={busy}>
                        Rotate now
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        </Frame>
      </Panel>
    </div>
  );
}

// ---------------------------------------------------------------------------

export function DeliveriesView() {
  const { data, error, loading, reload } = useLoad<{ data: Delivery[] }>(() => api.deliveries());
  const [busy, setBusy] = useState<string | null>(null);

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <Note>
        Replaying a delivery re-sends a notification that was already true. It does not re-create the
        underlying Stripe transaction and does not change the payment.
      </Note>

      <Frame loading={loading} error={error}>
        <Panel
          title="Deliveries"
          padded={false}
          actions={
            <button type="button" className="wc-btn wc-btn-sm wc-btn-secondary" onClick={() => void reload()}>
              <RefreshIcon size={13} /> Refresh
            </button>
          }
        >
          {data && data.data.length > 0 ? (
            <TableWrap>
              <table className="wc-table">
                <thead>
                  <tr><th>Event</th><th>Type</th><th>Endpoint</th><th>HTTP</th><th>Attempts</th><th>Status</th><th>Next try</th><th /></tr>
                </thead>
                <tbody>
                  {data.data.map((d) => (
                    <tr key={d.id}>
                      <td className="wc-num" style={{ fontSize: 12 }}>{shortId(d.event_id, 4)}</td>
                      <td style={{ fontSize: 13 }}>{d.event_type}{d.is_replay ? <Badge tone="info" icon={false}>REPLAY</Badge> : null}</td>
                      {/* The endpoint was truncated with an ellipsis, which is
                          exactly the thing you need to read in full when a
                          delivery is failing. Wrap it instead, and let it be
                          selected and copied. */}
                      <td style={{ fontSize: 12, maxWidth: 320, wordBreak: 'break-all', userSelect: 'all' }}>
                        <span title={d.endpoint}>{d.endpoint}</span>
                      </td>
                      <td className="wc-num">{d.http_status ?? '-'}</td>
                      <td className="wc-num">{d.attempts}</td>
                      <td>
                        <Badge tone={d.status === 'DELIVERED' ? 'ok' : d.status === 'PENDING' ? 'pending' : 'fail'}>
                          {d.status}
                        </Badge>
                      </td>
                      <td style={{ fontSize: 12.5, color: 'var(--wc-muted)' }}>
                        {d.next_attempt_at ? dateTime(d.next_attempt_at) : '-'}
                        {d.error ? (
                          <div style={{ fontSize: 11.5, color: 'var(--wc-red)', marginTop: 2, maxWidth: 240, wordBreak: 'break-word' }}>
                            {d.error}
                          </div>
                        ) : null}
                      </td>
                      <td className="wc-right">
                        <button
                          type="button"
                          className="wc-btn wc-btn-sm wc-btn-secondary"
                          disabled={busy === d.id}
                          onClick={async () => {
                            setBusy(d.id);
                            await api.replayDelivery(d.id);
                            await reload();
                            setBusy(null);
                          }}
                        >
                          Replay
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWrap>
          ) : (
            <Empty>Nothing delivered yet.</Empty>
          )}
        </Panel>
      </Frame>
    </div>
  );
}

// ---------------------------------------------------------------------------

export function ActivityView() {
  const { data, error, loading } = useLoad(() => api.activity());

  return (
    <Frame loading={loading} error={error}>
      <Panel padded={false}>
        {data && data.data.length > 0 ? (
          <TableWrap>
            <table className="wc-table">
              <thead><tr><th>When</th><th>Action</th><th>Actor</th><th>Subject</th></tr></thead>
              <tbody>
                {data.data.map((e, i) => (
                  <tr key={i}>
                    <td style={{ fontSize: 13, color: 'var(--wc-muted)', whiteSpace: 'nowrap' }}>{dateTime(e.at)}</td>
                    <td><strong style={{ fontSize: 13.5 }}>{e.action}</strong></td>
                    <td><Badge tone={e.actor === 'stripe_webhook' ? 'ok' : 'neutral'}>{e.actor}</Badge></td>
                    <td className="wc-num" style={{ fontSize: 12 }}>{shortId(e.subject, 5)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        ) : (
          <Empty>No activity recorded.</Empty>
        )}
      </Panel>
    </Frame>
  );
}
